/* eslint-disable react/no-unknown-property */
/* ============================================================
   Buildings & vegetation for the airport islands
   ControlTower : concrete shaft, slanted green-glass cab with
                  mullions, roof deck, rotating radar + beacon slot
   Terminal     : glazed hall with roof plant, canopy, jet bridge
   House        : gable roof, chimney, framed windows, door
   Tree         : pine / broadleaf / palm variants
   ============================================================ */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Text } from "@react-three/drei";
import * as THREE from "three";
import { getFacadeTexture, getStoneTexture } from "@/components/world/textures";

/* ---------- shared materials (lazy) ---------- */
let _m = null;
function M() {
  if (_m) return _m;
  const concrete = getStoneTexture("#9A958E", 17, 20);
  if (concrete) concrete.repeat.set(2, 3);
  const facade = getFacadeTexture("#3E5E7C", 0.18, 9);
  if (facade) facade.repeat.set(2, 1);
  _m = {
    concrete: new THREE.MeshStandardMaterial({ map: concrete || null, color: concrete ? "#FFFFFF" : "#9A958E", roughness: 0.9 }),
    concreteDark: new THREE.MeshStandardMaterial({ color: "#6E6A66", roughness: 0.9 }),
    white: new THREE.MeshStandardMaterial({ color: "#E9E5DD", roughness: 0.6 }),
    glassGreen: new THREE.MeshStandardMaterial({ color: "#1A3A34", metalness: 0.92, roughness: 0.06, emissive: "#2F6B5C", emissiveIntensity: 0.25 }),
    facade: new THREE.MeshStandardMaterial({ map: facade || null, color: facade ? "#FFFFFF" : "#24384C", metalness: 0.7, roughness: 0.25 }),
    steel: new THREE.MeshStandardMaterial({ color: "#C8CCD2", metalness: 0.9, roughness: 0.3 }),
    darkMetal: new THREE.MeshStandardMaterial({ color: "#2A2E36", metalness: 0.7, roughness: 0.45 }),
    roofGrey: new THREE.MeshStandardMaterial({ color: "#5C5F66", roughness: 0.85 }),
    wood: new THREE.MeshStandardMaterial({ color: "#3A2418", roughness: 1 }),
    bark: new THREE.MeshStandardMaterial({ color: "#4A3222", roughness: 1 }),
    palmBark: new THREE.MeshStandardMaterial({ color: "#8A6B4A", roughness: 1 }),
    leafDark: new THREE.MeshStandardMaterial({ color: "#23482C", roughness: 1 }),
    leafMid: new THREE.MeshStandardMaterial({ color: "#2F6136", roughness: 1 }),
    leafLight: new THREE.MeshStandardMaterial({ color: "#4A8A46", roughness: 1 }),
    palmLeaf: new THREE.MeshStandardMaterial({ color: "#3E8A3A", roughness: 0.9, side: THREE.DoubleSide }),
    windowGlass: new THREE.MeshStandardMaterial({ color: "#1E2A3C", metalness: 0.9, roughness: 0.08, emissive: "#FFE7B0", emissiveIntensity: 0.18 }),
    frame: new THREE.MeshStandardMaterial({ color: "#F4F0EA", roughness: 0.7 }),
    door: new THREE.MeshStandardMaterial({ color: "#5A2E1E", roughness: 0.8 }),
    brick: new THREE.MeshStandardMaterial({ color: "#7A4A3A", roughness: 0.95 }),
  };
  return _m;
}

/* ---------- geometry ---------- */
const ROOF_GEO = (() => {
  const s = new THREE.Shape();
  s.moveTo(-0.85, 0);
  s.lineTo(0.85, 0);
  s.lineTo(0, 0.62);
  s.lineTo(-0.85, 0);
  const g = new THREE.ExtrudeGeometry(s, { depth: 2.1, bevelEnabled: false });
  g.rotateY(Math.PI / 2); // extrude along X
  g.translate(-1.05, 0, 0);
  g.computeVertexNormals();
  return g;
})();
const PALM_FROND_GEO = (() => {
  // frond drawn along +y, then bent: extruded as a curved strip with a droop
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.quadraticCurveTo(0.55, 0.8, 0.18, 2.3);
  s.quadraticCurveTo(0, 2.5, -0.18, 2.3);
  s.quadraticCurveTo(-0.55, 0.8, 0, 0);
  const g = new THREE.ShapeGeometry(s, 8);
  // droop: bend the tip downward (z after rotation) as a function of distance
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    pos.setZ(i, -Math.pow(y / 2.5, 2) * 1.1);
  }
  g.rotateX(-Math.PI / 2); // +y → -z, bend → -y (droops downward)
  g.computeVertexNormals();
  return g;
})();

/* ============================================================
   Control tower
   ============================================================ */
export function ControlTower({ color, children }) {
  const radar = useRef();
  useFrame((state) => {
    if (radar.current) radar.current.rotation.y = state.clock.elapsedTime * 1.4;
  });
  const m = M();
  return (
    <group>
      <mesh position={[0, 0.2, 0]} material={m.concreteDark}>
        <cylinderGeometry args={[1.5, 1.7, 0.4, 16]} />
      </mesh>
      {/* shaft */}
      <mesh position={[0, 3.5, 0]} material={m.concrete}>
        <cylinderGeometry args={[0.72, 1.05, 6.6, 16]} />
      </mesh>
      {/* stair window strip */}
      <mesh position={[0, 3.4, 0.86]} material={m.windowGlass}>
        <boxGeometry args={[0.35, 5.6, 0.12]} />
      </mesh>
      {/* cab underside (flared) */}
      <mesh position={[0, 7.2, 0]} material={m.white}>
        <cylinderGeometry args={[1.95, 0.85, 0.9, 12]} />
      </mesh>
      {/* glazing – slanted outward */}
      <mesh position={[0, 8.3, 0]} material={m.glassGreen}>
        <cylinderGeometry args={[2.1, 1.9, 1.3, 10, 1, true]} />
      </mesh>
      {/* mullions */}
      {[...Array(10)].map((_, i) => {
        const a = (i / 10) * Math.PI * 2 + Math.PI / 10;
        return (
          <mesh key={i} position={[Math.cos(a) * 2.0, 8.3, Math.sin(a) * 2.0]} rotation={[0, -a, 0]} material={m.steel}>
            <boxGeometry args={[0.08, 1.34, 0.08]} />
          </mesh>
        );
      })}
      {/* roof deck + railing */}
      <mesh position={[0, 9.05, 0]} material={m.white}>
        <cylinderGeometry args={[2.25, 2.15, 0.25, 12]} />
      </mesh>
      <mesh position={[0, 9.5, 0]} rotation={[Math.PI / 2, 0, 0]} material={m.steel}>
        <torusGeometry args={[2.15, 0.03, 6, 32]} />
      </mesh>
      {/* radar */}
      <mesh position={[1.2, 9.6, 0]} material={m.steel}>
        <cylinderGeometry args={[0.06, 0.08, 0.9, 8]} />
      </mesh>
      <group ref={radar} position={[1.2, 10.1, 0]}>
        <mesh material={m.white} rotation={[0.3, 0, 0]}>
          <boxGeometry args={[1.6, 0.35, 0.06]} />
        </mesh>
      </group>
      {/* accent ring in the section colour */}
      <mesh position={[0, 7.65, 0]}>
        <cylinderGeometry args={[1.98, 1.98, 0.12, 12]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.6} />
      </mesh>
      {/* slot for the rotating beacon (RotatingBeacon is offset +6.4) */}
      <group position={[0, 3.0, 0]}>{children}</group>
    </group>
  );
}

/* ============================================================
   Terminal
   ============================================================ */
export function Terminal({ color, label = "GREINCH" }) {
  const m = M();
  return (
    <group>
      {/* main glazed hall */}
      <mesh position={[0, 1.4, 0]} material={m.facade}>
        <boxGeometry args={[6, 2.8, 8]} />
      </mesh>
      {/* structural white frame columns */}
      {[-3.6, -1.2, 1.2, 3.6].map((z, i) => (
        <mesh key={i} position={[3.02, 1.4, z]} material={m.white}>
          <boxGeometry args={[0.12, 2.8, 0.25]} />
        </mesh>
      ))}
      {/* roof slab with overhang + plant */}
      <mesh position={[0, 2.9, 0]} material={m.roofGrey}>
        <boxGeometry args={[6.6, 0.2, 8.6]} />
      </mesh>
      {/* coloured fascia band around the roof edge */}
      <mesh position={[0, 2.9, 0]}>
        <boxGeometry args={[6.7, 0.14, 8.7]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
      </mesh>
      <mesh position={[0, 3.0, 0]} material={m.roofGrey}>
        <boxGeometry args={[6.3, 0.08, 8.3]} />
      </mesh>
      {[
        [-1.6, -2.4],
        [0.9, -1.2],
        [-1.2, 1.9],
        [1.4, 2.6],
      ].map(([hx, hz], i) => (
        <mesh key={i} position={[hx, 3.4, hz]} material={m.darkMetal}>
          <boxGeometry args={[1.1, 0.6, 1.0]} />
        </mesh>
      ))}
      {/* entrance canopy (land side, -x) */}
      <mesh position={[-3.6, 2.2, 0]} material={m.steel}>
        <boxGeometry args={[1.6, 0.1, 5]} />
      </mesh>
      {[-2, 2].map((z) => (
        <mesh key={z} position={[-4.2, 1.1, z]} material={m.steel}>
          <cylinderGeometry args={[0.06, 0.06, 2.2, 8]} />
        </mesh>
      ))}
      {/* jet bridge to the apron (+x) */}
      <mesh position={[4.6, 1.9, -1.5]} material={m.white}>
        <boxGeometry args={[3.2, 0.9, 0.9]} />
      </mesh>
      <mesh position={[6.3, 1.9, -1.5]} material={m.white}>
        <cylinderGeometry args={[0.7, 0.7, 1.1, 12]} />
      </mesh>
      <mesh position={[6.3, 0.8, -1.5]} material={m.darkMetal}>
        <cylinderGeometry args={[0.15, 0.15, 1.6, 8]} />
      </mesh>
      {/* baggage carts */}
      {[
        [4.4, 2.2],
        [5.2, 2.9],
      ].map(([cx, cz], i) => (
        <group key={i} position={[cx, 0.25, cz]} rotation={[0, i * 0.7, 0]}>
          <mesh material={m.darkMetal}>
            <boxGeometry args={[0.9, 0.35, 0.5]} />
          </mesh>
          <mesh position={[0, 0.3, 0]}>
            <boxGeometry args={[0.8, 0.3, 0.45]} />
            <meshStandardMaterial color={i === 0 ? "#E94B4B" : "#F0A020"} roughness={0.7} />
          </mesh>
        </group>
      ))}
      {/* signage */}
      <Text position={[-3.12, 2.45, 0]} rotation={[0, -Math.PI / 2, 0]} fontSize={0.42} color="#F4EADE" anchorX="center" anchorY="middle" letterSpacing={0.18}>
        {label}
      </Text>
    </group>
  );
}

/* ============================================================
   House
   ============================================================ */
export function House({ roofColor = "#B34A3A", seed = 0 }) {
  const m = M();
  const roofMat = useMemo(() => new THREE.MeshStandardMaterial({ color: roofColor, roughness: 0.9 }), [roofColor]);
  const walls = ["#E8D6BE", "#F2E9DC", "#D9C7A8", "#E3D2C6"][seed % 4];
  const wallMat = useMemo(() => new THREE.MeshStandardMaterial({ color: walls, roughness: 0.9 }), [walls]);
  const renderWindow = (key, position, rotation = [0, 0, 0]) => (
    <group key={key} position={position} rotation={rotation}>
      <mesh material={m.frame}>
        <boxGeometry args={[0.46, 0.42, 0.06]} />
      </mesh>
      <mesh position={[0, 0, 0.02]} material={m.windowGlass}>
        <boxGeometry args={[0.36, 0.32, 0.04]} />
      </mesh>
      <mesh position={[0, 0, 0.04]} material={m.frame}>
        <boxGeometry args={[0.03, 0.34, 0.02]} />
      </mesh>
    </group>
  );
  return (
    <group>
      {/* foundation */}
      <mesh position={[0, 0.05, 0]} material={m.concreteDark}>
        <boxGeometry args={[1.95, 0.1, 1.55]} />
      </mesh>
      <mesh position={[0, 0.6, 0]} material={wallMat}>
        <boxGeometry args={[1.8, 1.1, 1.4]} />
      </mesh>
      {/* gable roof */}
      <mesh geometry={ROOF_GEO} material={roofMat} position={[0, 1.13, 0]} />
      {/* eaves slabs */}
      <mesh position={[0, 1.44, 0.46]} rotation={[0.63, 0, 0]} material={roofMat}>
        <boxGeometry args={[2.15, 0.06, 1.05]} />
      </mesh>
      <mesh position={[0, 1.44, -0.46]} rotation={[-0.63, 0, 0]} material={roofMat}>
        <boxGeometry args={[2.15, 0.06, 1.05]} />
      </mesh>
      {/* chimney */}
      <mesh position={[0.55, 1.65, -0.25]} material={m.brick}>
        <boxGeometry args={[0.25, 0.6, 0.25]} />
      </mesh>
      {/* door */}
      <mesh position={[-0.45, 0.4, 0.71]} material={m.door}>
        <boxGeometry args={[0.4, 0.72, 0.05]} />
      </mesh>
      <mesh position={[-0.45, 0.02, 0.85]} material={m.concreteDark}>
        <boxGeometry args={[0.6, 0.06, 0.3]} />
      </mesh>
      {/* windows */}
      {renderWindow("w1", [0.35, 0.65, 0.71])}
      {renderWindow("w2", [0.91, 0.65, 0.25], [0, Math.PI / 2, 0])}
      {renderWindow("w3", [-0.91, 0.65, -0.25], [0, -Math.PI / 2, 0])}
      {renderWindow("w4", [0.2, 0.65, -0.71], [0, Math.PI, 0])}
    </group>
  );
}

/* ============================================================
   Tree – variant 0 pine, 1 broadleaf, 2 palm
   ============================================================ */
export function Tree({ variant = 0, size = 1 }) {
  const m = M();
  if (variant === 2) {
    return (
      <group scale={size}>
        <mesh position={[0.1, 1.6, 0]} rotation={[0, 0, -0.08]} material={m.palmBark}>
          <cylinderGeometry args={[0.1, 0.17, 3.3, 8]} />
        </mesh>
        <group position={[0.28, 3.25, 0]}>
          {[...Array(9)].map((_, i) => {
            const a = (i / 9) * Math.PI * 2;
            return (
              <group key={i} rotation={[0, a, 0]}>
                <mesh geometry={PALM_FROND_GEO} material={m.palmLeaf} rotation={[0.35 - (i % 2) * 0.3, 0, 0]} scale={[0.9, 1, 1]} />
              </group>
            );
          })}
          <mesh material={m.palmBark}>
            <sphereGeometry args={[0.18, 8, 6]} />
          </mesh>
          {/* coconuts */}
          {[0, 1, 2].map((i) => (
            <mesh key={i} position={[Math.cos(i * 2.1) * 0.15, -0.1, Math.sin(i * 2.1) * 0.15]}>
              <sphereGeometry args={[0.07, 6, 6]} />
              <meshStandardMaterial color="#5A3A20" roughness={1} />
            </mesh>
          ))}
        </group>
      </group>
    );
  }
  if (variant === 1) {
    return (
      <group scale={size}>
        <mesh position={[0, 0.9, 0]} material={m.bark}>
          <cylinderGeometry args={[0.14, 0.24, 1.8, 7]} />
        </mesh>
        <mesh position={[0, 2.3, 0]} material={m.leafMid}>
          <sphereGeometry args={[1.25, 10, 8]} />
        </mesh>
        <mesh position={[0.7, 2.0, 0.3]} material={m.leafDark}>
          <sphereGeometry args={[0.85, 9, 7]} />
        </mesh>
        <mesh position={[-0.6, 2.2, -0.4]} material={m.leafLight}>
          <sphereGeometry args={[0.8, 9, 7]} />
        </mesh>
        <mesh position={[0.1, 2.95, -0.2]} material={m.leafLight}>
          <sphereGeometry args={[0.7, 9, 7]} />
        </mesh>
      </group>
    );
  }
  return (
    <group scale={size}>
      <mesh position={[0, 0.8, 0]} material={m.bark}>
        <cylinderGeometry args={[0.13, 0.22, 1.6, 7]} />
      </mesh>
      <mesh position={[0, 2.0, 0]} material={m.leafDark}>
        <coneGeometry args={[1.45, 1.9, 9]} />
      </mesh>
      <mesh position={[0, 3.0, 0]} material={m.leafMid}>
        <coneGeometry args={[1.1, 1.7, 9]} />
      </mesh>
      <mesh position={[0, 3.9, 0]} material={m.leafLight}>
        <coneGeometry args={[0.7, 1.4, 9]} />
      </mesh>
    </group>
  );
}
